import {cn} from '@tempmee/core'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {Row} from '../box'

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  /** Label text displayed above the input */
  label?: string
  /** Links the label to the associated input via htmlFor */
  htmlFor?: string
  /** Appends a red asterisk to the label */
  required?: boolean
  /** Appends "(Optional)" to the label */
  optional?: boolean
  /** Font weight of the label (default: 'bold') */
  labelWeight?: 'bold' | 'normal'
  /** Component rendered to the left of the label */
  LeadingComponent?: ReactNode
  /** Component rendered to the right of the label */
  TrailingComponent?: ReactNode
  children: ReactNode
}

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      className,
      label,
      htmlFor,
      required,
      optional,
      labelWeight = 'bold',
      LeadingComponent,
      TrailingComponent,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
        {label && (
          <Row align="center" gap={2}>
            {LeadingComponent}
            <label
              htmlFor={htmlFor}
              className={cn(
                'text-base text-text-primary',
                labelWeight === 'normal' ? 'font-normal' : 'font-bold',
              )}
            >
              {label}
              {required && <span className="text-error"> *</span>}
              {optional && <span className="font-normal text-text-secondary"> (Optional)</span>}
            </label>
            {TrailingComponent}
          </Row>
        )}
        {children}
      </div>
    )
  },
)

FormField.displayName = 'FormField'
